import { Badge, Filter } from '@vrplatform/components';
import { useQuery, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { LazyTable, type MRT_ColumnDef } from '@vrplatform/table';
import { parseAsString, useQueryStates } from '@vrplatform/query-params';
import { type Maybe, toTitleCase } from '@vrplatform/utils';
import {
  Alert,
  Box,
  Center,
  Group,
  LoadingOverlay,
  Text,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import type {
  accountClassification_enum,
  activeStatus_enum,
} from '@vrplatform/graphql';
import { formatAccountType } from '@vrplatform/ui-common';
import { Fragment, useCallback, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useFeeForm } from '../useFeeForm';
import { CustomFilter } from '../../../../components';
import { shouldRequireFeeLinkedAccounts } from './FeeAmountFilterEditor.utils';

type Props = {
  initialAccountIds: string[];
};

export function useFeeAccountsQuery(props: Props) {
  const [{ id: teamId }] = useTeam();

  const methods = useFeeForm();

  const feeType = useWatch({
    control: methods.control,
    name: 'type',
  });

  const queryData = useQuery(
    (q, args) => {
      const accounts = q
        .accounts({
          where: {
            tenantId: { _eq: args.teamId },
          },
          order_by: [{ title: 'asc_nulls_last' }],
        })
        .map((account) => ({
          id: account.id,
          title: account.title || '-',
          status: account.status,
          type: account.type,
          classification: account.category?.classification,
          assignments: account
            .assignments({
              where: {
                tenantId: { _eq: args.teamId },
              },
            })
            .map((x) => x.type!),
          hasJournalEntry: !!account
            .journalEntries({
              limit: 1,
              where: {
                status: { _eq: 'active' },
                account: {
                  type: { _eq: 'ledger' },
                },
              },
            })
            .map((x) => x.id).length,
          lineItems: account
            .reservationLineTypes({
              where: {
                tenantId: { _eq: args.teamId },
                status: { _eq: 'active' },
                lineType: { _is_null: false },
              },
              order_by: [{ lineType: 'asc_nulls_last' }],
            })
            .map((mapping) => {
              const lineType = mapping.lineType || '';
              const hasApp = lineType.includes('_');
              const app = hasApp ? lineType.split('_')[0] : null;
              const line = toTitleCase(
                hasApp ? lineType.split('_')[1] : lineType
              );

              return {
                lineType: lineType!,
                app,
                line,
              };
            }),
        }));

      return accounts
        .filter((account) => {
          if (args.initialAccountIds.includes(account.id)) {
            return true;
          }

          // for custom fees, return all accounts of type ledger EXCEPT the ones have assignment: Accounts Receivable or Accounts Payable
          // if (args.feeType === 'additionalFee') {
          return (
            account.status === 'active' &&
            (account.type === 'ledger' ||
              account.type === 'recurringFee' ||
              account.type === 'nonPosting') &&
            account.assignments.includes('accountsPayable') === false &&
            account.assignments.includes('accountsReceivable') === false
          );
          // }

          // // for all other fees, return all accounts of type ledger that have a journal entry
          // return account.hasJournalEntry && account.status === 'active';
        })
        .sort((a, b) => {
          const order: Record<accountClassification_enum | '-', number> = {
            revenue: 1,
            expense: 2,
            liability: 3,
            asset: 4,
            '-': 5,
          };

          return (
            order[a.classification ?? '-'] - order[b.classification ?? '-']
          );
        });
    },
    {
      variables: {
        teamId,
        initialAccountIds: props.initialAccountIds,
        feeType,
      },
      queryKey: ['accounts'],
      keepPreviousData: true,
    }
  );

  return {
    data: queryData.data ?? [],
    isLoading: queryData.isLoading,
    isFetching: queryData.isFetching,
    error: queryData.error,
  };
}

export type FeeAccountRow = ReturnType<
  typeof useFeeAccountsQuery
>['data'][number];

export function useFeeAccountsTableFilter() {
  const [filter, setFilter] = useQueryStates({
    search: parseAsString,
    line: parseAsString,
    status: parseAsString,
  });

  return {
    filter,
    setFilter,
    resetFilter: useCallback(
      () => setFilter({ search: '', line: null }),
      [setFilter]
    ),
  };
}

export const FeeAccountsTable = (props: Props) => {
  const methods = useFeeForm();
  const { colorScheme } = useMantineColorScheme();
  const { colors } = useMantineTheme();

  const { filter, resetFilter } = useFeeAccountsTableFilter();

  const rateType = useWatch({
    control: methods.control,
    name: 'rateType',
  });
  const amountFilter = useWatch({
    control: methods.control,
    name: 'accountTotalConditions',
  });
  const linkedAccountsRequired = shouldRequireFeeLinkedAccounts({
    rateType,
    amountFilter,
  });

  const queryData = useFeeAccountsQuery(props);

  const trimmed = filter.search?.trim();

  const columns = useMemo<MRT_ColumnDef<FeeAccountRow>[]>(
    () => [
      {
        accessorKey: 'title',
        header: 'Account',
        AggregatedCell: ({ row }) => {
          const classification = row.original.classification;
          return (
            <Text component="p" size="sm" fw={500}>
              {toTitleCase(classification) || 'No classification'} (
              {row.subRows?.length ?? 0})
            </Text>
          );
        },
        Cell: ({ row }) => {
          return (
            <Box>
              <Group wrap="nowrap" gap={rem(8)}>
                <Text>{row.original.title}</Text>
                {row.original.type === 'nonPosting' && (
                  <Badge size="sm">{formatAccountType('nonPosting')}</Badge>
                )}
              </Group>
              <Text c="gray" size="xs">
                Included financials:{' '}
                {row.original.lineItems?.length
                  ? row.original.lineItems.map((item, index, arr) => {
                      const hasNext = index < arr.length - 1;

                      return (
                        <Fragment key={item.lineType}>
                          <Tooltip label={item.lineType} withArrow>
                            <Text size="xs" span>
                              {item.line}
                            </Text>
                          </Tooltip>
                          {hasNext && (
                            <Text size="xs" span>
                              ,{' '}
                            </Text>
                          )}
                        </Fragment>
                      );
                    })
                  : '-'}
              </Text>
            </Box>
          );
        },
      },
      {
        id: 'classification',
        header: 'Classification',
        minSize: 200,
        enableColumnDragging: false,
        getGroupingValue: (row) => row.classification,
        enableGrouping: true,
        GroupedCell: ({ row }) => row.original.classification,
        maxSize: 70,
        Cell: () => {
          return null;
        },
        enableHiding: false,
      },
    ],
    []
  );

  return (
    <>
      <LoadingOverlay
        visible={rateType === 'flat' && !linkedAccountsRequired}
        zIndex={100}
        loaderProps={{
          children: (
            <Box
              sx={{
                textAlign: 'center',
              }}
            >
              <Icon icon="BookOpenIcon" mb="xs" />
              <Text c="neutral" fw={500} size="md" mb="xs">
                Accounts not required
              </Text>
              <Text c="neutral" mx="auto" maw={350}>
                Flat fees do not require account selection and are directly
                applied to the reservation.
              </Text>
            </Box>
          ),
        }}
      />
      <Controller
        control={methods.control}
        name="linkedAccountIds"
        rules={{
          validate: (value) => {
            if (!value?.length && linkedAccountsRequired)
              return 'At least one account is required';
            return true;
          },
        }}
        render={({ field, fieldState }) => {
          const value = field.value ?? [];

          // biome-ignore lint/correctness/useExhaustiveDependencies: leaving out field.value on purpose
          // biome-ignore lint/correctness/useHookAtTopLevel: no need inside Controller render
          const options = useMemo(() => {
            const data = queryData.data ?? [];

            const status = filter.status as Maybe<activeStatus_enum>;

            const hasFilter = trimmed || filter.line || status;

            if (hasFilter) {
              return data.filter((account) => {
                const inTitle = !trimmed
                  ? true
                  : account.title.toLowerCase().includes(trimmed.toLowerCase());

                const inMappings = !trimmed
                  ? true
                  : account.lineItems.some((mapping) => {
                      return mapping.line
                        .toLowerCase()
                        .includes(trimmed.toLowerCase());
                    });

                const inLineType = !filter.line
                  ? true
                  : account.lineItems.some((mapping) => {
                      return mapping.lineType === filter.line;
                    });

                const inStatus = !status
                  ? true
                  : !!value.includes(account.id) === (status === 'active');

                return (inTitle || inMappings) && inLineType && inStatus;
              });
            }

            return data;
          }, [queryData.data, trimmed, filter.line, filter.status]);

          return (
            <>
              <Box
                sx={(theme) =>
                  Object.assign({
                    backgroundColor:
                      colorScheme === 'dark'
                        ? theme.colors.dark[6]
                        : theme.colors.neutral[1],
                    padding: '0.4rem 0.7rem',
                    borderRadius: theme.radius.md,
                    // minHeight: 35,
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: theme.spacing.md,
                  })
                }
              >
                <Text size="sm" fw={500}>
                  Accounts included in the fee
                  <Text
                    c={colorScheme === 'dark' ? colors.gray[6] : 'gray'}
                    ml="xs"
                    size="xs"
                    component="span"
                  >
                    {value.length}
                  </Text>
                </Text>
              </Box>

              {fieldState.error && (
                <Alert title={'Validation'} color="red">
                  {fieldState.error.message}
                </Alert>
              )}
              <LazyTable
                data={{
                  loading: queryData.isLoading,
                  rows: options,
                  rowCount: options.length,
                  error: queryData.error?.message,
                }}
                table={{
                  columns,
                  hideSettings: true,
                  hideHeader: true,
                  emptyRowsFallback: () => (
                    <Center>
                      <Text size="sm" c="gray">
                        No accounts found
                      </Text>
                    </Center>
                  ),
                }}
                styles={{
                  row: ({ row }, theme) => {
                    if (row.getIsGrouped())
                      return {
                        backgroundColor: `${theme.colors.neutral?.[colorScheme === 'dark' ? 9 : 1]}!important`,
                        height: 45,
                      };

                    return {};
                  },
                }}
                groupBy={{
                  groupBy: ['classification'],
                  setGroupBy: undefined,
                  allowExpanding: true,
                  defaultExpanded: true,
                }}
                pagination={{
                  pagination: {
                    pageIndex: 0,
                    pageSize: options.length,
                  },
                  setPagination: undefined,
                }}
                selecting={{
                  rowSelection: {
                    rows:
                      field.value?.reduce(
                        (acc, id) => {
                          acc[id] = true;
                          return acc;
                        },
                        {} as Record<string, boolean>
                      ) ?? {},
                    allPagesSelected: false,
                  },
                  enableRowSelection: () =>
                    methods.formState.disabled ? false : linkedAccountsRequired,
                  disableSelectAll: true,
                  setRowSelection: (select) => {
                    const ids = Object.entries(select)
                      .filter(([_, selected]) => selected)
                      .map(([id]) => id);

                    field.onChange(ids);
                  },
                  setAllPagesSelected: () => {},
                }}
                resetFilter={resetFilter}
              >
                <Group>
                  <SearchFilter />
                  <StatusFilter />
                  <LineTypeFilter />
                </Group>
              </LazyTable>
            </>
          );
        }}
      />
    </>
  );
};

const SearchFilter = () => {
  const { filter, setFilter } = useFeeAccountsTableFilter();

  return (
    <Filter.Search
      value={filter.search || ''}
      setValue={(value) => setFilter({ search: value })}
    />
  );
};

const LineTypeFilter = () => {
  const { filter, setFilter } = useFeeAccountsTableFilter();

  return (
    <CustomFilter.ReservationLineType
      lineType={filter.line}
      setLineType={(line) => setFilter({ line })}
    />
  );
};

const StatusFilter = () => {
  const { filter, setFilter } = useFeeAccountsTableFilter();

  return (
    <Filter.Status
      value={(filter.status as Maybe<activeStatus_enum>) ?? null}
      setValue={(value) => setFilter({ status: value ?? null })}
      activeLabel="Mapped"
      inactiveLabel="Unmapped"
      clearable
      label="Status"
    />
  );
};
